"use client";

import dayjs from "dayjs";
import dynamic from "next/dynamic";

import { ErrorView } from "@/components/error-view";
import { useSymbolPrices } from "@/features/analysis/backtest/services/symbol-prices";
import { useLang } from "@/hooks/use-lang";

import { ChartSkeletons } from "ui/widget/skeletons/Skeletons";

import { getTop5PlotData } from "../data/top5-plot-data";
import { Top5Index } from "../data/types";
import { useInsiderBuyingTop5 } from "../services/insider-buying-top5";

const ULineChart = dynamic(
  () => import("ui/analysis/u-line-chart").then((mod) => mod.ULineChart),
  { ssr: false, loading: () => <ChartSkeletons /> }
);

type Props = {
  symbol: string;
  top5: Top5Index;
};

export const Top5Chart = ({ symbol, top5 }: Props) => {
  const { t, lang } = useLang();
  const yearAgo = dayjs().subtract(1, "year").format("YYYY-MM-DD");
  const {
    data: pricesData,
    isLoading: isLoadingPrices,
    isError: isErrorPrices,
  } = useSymbolPrices({
    symbol,
    start_at: yearAgo,
    end_at: dayjs().format("YYYY-MM-DD"),
  });
  const {
    data: buyingTop5,
    isLoading: isLoadingTop5,
    isError: isErrorTop5,
  } = useInsiderBuyingTop5({ symbol });

  const selectedTop5 = buyingTop5?.dropDown?.[top5]?.member ?? "";
  const tradeInfoData = buyingTop5?.point?.[selectedTop5] ?? [];
  const plotData = getTop5PlotData({
    tradeInfoData,
    pricesData: pricesData ?? [],
  });

  return isLoadingPrices || isLoadingTop5 ? (
    <ChartSkeletons />
  ) : isErrorPrices || isErrorTop5 ? (
    <ErrorView className="h-80">{t.error.serverError}</ErrorView>
  ) : tradeInfoData.length === 0 ? null : (
    <ULineChart
      data={plotData}
      dataInfo={[
        {
          label: t.analysis.shared.price,
          valueFormatter: (value) => `$${+(value as number).toFixed(2)}`,
          strokeColor: "#5A8FD6",
          strokeWidth: 2,
        },
      ]}
      lang={lang}
      height={360}
      dateLabel={t.analysis.shared.date}
      showSignal
      showLegend="none"
      showTooltip
    />
  );
};
